import type { NodeTree, TreeNode } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { indexTree, owningUnitOf, pathNamesOf, typeOf } from './node-tree-path.js';

/**
 * 归档。
 *
 * 归档的资产从树和卡片区退出去了，但没有删——执行记录要引用它依据的那一份标准。
 * 所以得有一个地方看得见它们、能取消归档，否则归档就等于弄丢。
 *
 * 只列资产。组织节点的 enabled=false 是「停用」，不是归档：它还留在组织架构页上
 * 标着停用，管理它的地方就在那儿。
 */
export function ArchivePage({ rootName }: { rootName: string }): ReactNode {
  const [tree, setTree] = useState<NodeTree | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function reload(): void {
    void api.tree().then(
      (data) => {
        setTree(data);
        setListError(null);
      },
      (cause: unknown) => setListError(messageOf(cause)),
    );
  }

  useEffect(reload, []);

  async function restore(node: TreeNode): Promise<void> {
    setBusyId(node.id);
    setActionError(null);
    try {
      // 归档与恢复现在是领域表上的 status，不再是 Node 的 enabled（C-077）。
      // 树只是投影，投影没有写路径——改的必须是源头那张表。
      if (node.typeCode === 'DEPARTMENT') {
        await api.changeDepartmentStatus(node.id, 'active', '在归档页恢复');
      } else if (node.typeCode === 'POSITION') {
        await api.changePositionStatus(node.id, 'active', '在归档页恢复');
      } else {
        throw new Error('ATC 的归档与恢复走它自己的详情页');
      }
      reload();
    } catch (cause) {
      setActionError(messageOf(cause));
    } finally {
      setBusyId(null);
    }
  }

  if (listError !== null) return <p className="msg">{listError}</p>;
  if (tree === null) return <p className="sub-hint">读取中……</p>;

  const index = indexTree(tree);
  const archived = tree.nodes.filter(
    (node) => !node.enabled && typeOf(index, node)?.kind === 'ASSET',
  );

  return (
    <div className="content-in">
      <div className="page-head">
        <div>
          <h1>归档</h1>
          <p className="sub-hint">
            归档的资产不在树和卡片区里显示，但一直留着——执行记录要引用它依据的那一份标准。
          </p>
        </div>
      </div>

      {actionError !== null && <p className="msg">{actionError}</p>}

      {archived.length === 0 ? (
        <p className="sub-hint">还没有归档的东西。在 ATC 的详情页可以归档。</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名称</th>
                <th>类型</th>
                <th>编码</th>
                <th>归属</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {archived.map((node) => {
                const owner = owningUnitOf(index, node.id);
                return (
                  <tr key={node.id}>
                    <td>{node.name}</td>
                    <td>{typeOf(index, node)?.name ?? node.typeCode}</td>
                    <td className="mono">{node.code ?? '—'}</td>
                    <td>
                      {owner === null
                        ? `${rootName}（全公司通用）`
                        : pathNamesOf(index, owner.id, rootName).slice(1).join(' / ')}
                    </td>
                    <td className="actions">
                      <button
                        className="linkbtn"
                        type="button"
                        disabled={busyId === node.id}
                        onClick={() => void restore(node)}
                      >
                        {busyId === node.id ? '恢复中' : '取消归档'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
